'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@eliadmin/core/src/components/ui/select';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { changeLanguage } from '@eliadmin/core/src/server/api-client';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

export function LanguageSelect() {
  const [isLoading, setIsLoading] = useState(false);
  const locale = useLocaleStore(s => s.currentLocale);
  const locales = useLocaleStore(s => s.supportedLocales);
  const pathname = usePathname();

  const handleLocaleChange = async (newLocale: string) => {
    let newRoute = `/${newLocale}${pathname}`;
    if (pathname.startsWith(`/${locale}`)) {
      newRoute = pathname.replace(`/${locale}`, `/${newLocale}`);
    }
    setIsLoading(true);
    await changeLanguage({ locale: newLocale });
    window.location.replace(newRoute);
  };

  return (
    <div className={cn('w-auto', 'ea-language-select')}>
      <Select value={locale} onValueChange={handleLocaleChange}>
        <SelectTrigger
          aria-label={locales.find(l => l.locale === locale)?.name || locale}
          className={cn('border-none text-white outline-none', 'ea-language-select__trigger')}
          disabled={isLoading}
        >
          {isLoading ? (
            <Spinner className={cn('', 'ea-language-select__spinner')} />
          ) : (
            <span className={cn('px-2', 'ea-language-select__flag')}>
              <Image
                src={locales.find(l => l.locale === locale)?.image?.url || '/images/placeholder.png'}
                alt={resolveImageAlt(locales?.find?.(l => l.locale === locale)?.image, locales?.find?.(l => l.locale === locale)?.name)}
                height={0}
                width={0}
                className={cn('', 'ea-language-select__flag-image')}
              />
            </span>
          )}
        </SelectTrigger>
        <SelectContent className={cn('text-white', 'ea-language-select__content')}>
          {locales.map(locale => (
            <SelectItem
              key={`${locale.name}-${locale.locale}`}
              value={locale.locale}
              aria-label={locale.name || locale.locale}
              className={cn('', 'ea-language-select__item')}
            >
              <Image
                src={locale.image?.url || '/images/placeholder.png'}
                alt={resolveImageAlt(locale?.image, locale?.name)}
                height={0}
                width={0}
                className={cn('', 'ea-language-select__flag-image')}
              />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
